import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Loader2, ChevronLeft, ChevronRight, X } from 'lucide-react';
import SkuProductCard from './SkuProductCard';

interface Product {
  id_catalogo: string;
  title?: string;
  sitio?: string;
  sale_price?: string | number;
  imagen?: string;
  [key: string]: any;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface ProductSearchColumnProps {
  searchResults: Product[];
  skuProductIds: Set<string>;
  isLoading: boolean;
  isSearching: boolean;
  canAddProducts: boolean;
  pagination?: Pagination;
  onSearch: (terms: string, page?: number) => void;
  onAddProduct: (product: Product) => void;
  onPageChange?: (page: number) => void;
  onClearSearch?: () => void;
  resetCounter?: number;
}

const ProductSearchColumn: React.FC<ProductSearchColumnProps> = ({
  searchResults,
  skuProductIds,
  isLoading,
  isSearching,
  canAddProducts,
  pagination,
  onSearch,
  onAddProduct,
  onPageChange,
  onClearSearch,
  resetCounter = 0,
}) => {
  const { t } = useTranslation();
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    setSearchInput('');
  }, [resetCounter]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchInput.trim()) {
      onSearch(searchInput.trim(), 1);
    }
  };

  // Filter out products that are already in the SKU
  const filteredResults = searchResults.filter(
    (product) => !skuProductIds.has(product.id_catalogo)
  );

  return (
    <div className="flex flex-col h-full bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="p-3 border-b border-gray-200 bg-gray-50">
        <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
          <Search className="h-4 w-4" />
          {t('skus.search_products_header')}
          {pagination && pagination.total > 0 && (
            <span className="text-xs font-normal text-gray-500">
              ({pagination.total})
            </span>
          )}
        </h3>
        {/* Search form */}
        <form onSubmit={handleSubmit} className="flex gap-2">
          <div className="relative flex-1">
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t('skus.search_terms_placeholder')}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent pr-12"
            />
            {isSearching && (
              <Loader2 className="absolute right-10 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 animate-spin" />
            )}
            {searchInput && (
              <button
                type="button"
                onClick={() => {
                  setSearchInput('');
                  onClearSearch?.();
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                title={t('skus.clear_search')}
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <button
            type="submit"
            disabled={!searchInput.trim() || isSearching}
            className="px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('skus.search_button')}
          </button>
        </form>
        <p className="mt-1 text-xs text-gray-500">
          {t('skus.search_example')}
        </p>
      </div>

      {/* Results */}
      <div className="flex-1 min-h-0 overflow-y-auto p-3">
        {isLoading || isSearching ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-pink-500"></div>
          </div>
        ) : searchResults.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            <Search className="h-12 w-12 mb-2" />
            <p className="text-sm text-center">
              {t('skus.search_to_add')}
            </p>
          </div>
        ) : filteredResults.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            <Search className="h-12 w-12 mb-2" />
            <p className="text-sm text-center">
              {t('skus.all_results_in_sku')}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredResults.map((product) => (
              <SkuProductCard
                key={product.id_catalogo}
                product={product}
                onAdd={canAddProducts ? onAddProduct : undefined}
                isDraggable={canAddProducts}
                showAddButton={canAddProducts}
                showDragHandle={true}
              />
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="px-3 py-2 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
          {(() => {
            const start = (pagination.page - 1) * pagination.limit + 1;
            const end = pagination.total
              ? Math.min(pagination.page * pagination.limit, pagination.total)
              : pagination.page * pagination.limit;
            const total = pagination.total ?? '—';
            return (
              <span className="text-[11px] text-gray-500">
                {start} {t('skus.pagination.from')} {end} {t('skus.pagination.of')} {total} · {t('skus.pagination.page')} {pagination.page} {t('skus.pagination.of_pages')} {pagination.totalPages}
              </span>
            );
          })()}
          <button
            onClick={() => onPageChange?.(pagination.page - 1)}
            disabled={pagination.page <= 1 || isSearching}
            className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            onClick={() => onPageChange?.(pagination.page + 1)}
            disabled={pagination.page >= pagination.totalPages || isSearching}
            className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Footer info */}
      {(!pagination || pagination.totalPages <= 1) && (
        <div className="p-2 border-t border-gray-200 bg-gray-50 text-center text-xs text-gray-500">
          {filteredResults.length > 0 && (
            <>
              {filteredResults.length} {filteredResults.length !== 1 ? t('skus.results_count_plural') : t('skus.results_count')}
              {searchResults.length !== filteredResults.length && (
                <span className="text-pink-500 ml-1">
                  ({searchResults.length - filteredResults.length} {t('skus.already_in_sku')})
                </span>
              )}
            </>
          )}
          {!canAddProducts && searchResults.length > 0 && (
            <span className="text-amber-600 block mt-1">
              {t('skus.select_sku_to_add')}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductSearchColumn;
